import type { AgentNodeData } from '@saaga/shared/interfaces/agency-node.interface'
import { Handle, Position } from '@xyflow/react'
import { Trash2Icon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { withStopPropagation } from '@/lib/event.util'
import { cn } from '@/lib/utils'

import { AgentNodeIcon } from './agent-node-icon.atom'

export interface AgentNodeAtomProps {
  id: string
  data: AgentNodeData
  selected?: boolean
  onDelete?: (nodeId: string) => void
}

export const AgentNodeAtom = memo(
  ({ id, data, selected, onDelete }: AgentNodeAtomProps): ReactNode => (
    <div
      className={cn(
        'agent-node relative rounded-lg border-2 bg-background shadow-md transition-all',
        'min-w-[200px] max-w-[220px]',
        selected ? 'border-primary ring-2 ring-primary/20' : 'border-border',
        data.isEntryPoint && 'border-amber-500'
      )}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="agent-node-handle-target !size-3 !border-2 !border-background !bg-muted-foreground"
        isConnectable={!data.isEntryPoint}
      />
      <div className="agent-node-content flex flex-col gap-1 p-3">
        <div className="agent-node-header flex items-center gap-2">
          <AgentNodeIcon isEntryPoint={data.isEntryPoint} />
          <div className="agent-node-info min-w-0 flex-1">
            <p className="agent-node-label truncate text-sm font-medium">{data.label}</p>
            {data.isEntryPoint && (
              <p className="agent-node-role text-xs text-amber-600">Entry Point</p>
            )}
          </div>
          {!data.isEntryPoint && onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="agent-node-delete size-6 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={withStopPropagation(() => onDelete(id))}
            >
              <Trash2Icon className="size-3.5" />
            </Button>
          )}
        </div>
        <div className="agent-node-meta mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="agent-node-model truncate">{data.model.split('/')[1]}</span>
          <span className="agent-node-temp">T: {data.temperature}</span>
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="agent-node-handle-source !size-3 !border-2 !border-background !bg-primary"
      />
    </div>
  )
)

AgentNodeAtom.displayName = 'AgentNodeAtom'
